/**
 * A GPX file, as a list of coordinates.
 *
 * GPX is the format every watch and training platform exports, and an organiser
 * uploads one so participants can load the course onto a device. The same file
 * is the most accurate description of the route anyone has, so it is also what
 * the map draws — better than the straight line between two addresses, which is
 * all the start and finish pins can say.
 *
 * Three shapes carry points, in the order preferred here:
 *   <trkpt> — a recorded or drawn track, what almost every export contains
 *   <rtept> — a planned route
 *   <wpt>   — loose waypoints, the last resort
 *
 * Parsed with DOMParser rather than a library: this is four lines of DOM
 * walking, and a public bundle should not carry a GPX dependency for it.
 * Anything unparseable returns null and the page simply shows no route.
 */

import parsePoint from './geoPoint';

const POINT_TAGS = ['trkpt', 'rtept', 'wpt'];
const MIN_POINTS = 2;

function hasTwoDistinct(points) {
  const [first] = points;
  return points.some(p => p.lat !== first.lat || p.lng !== first.lng);
}

export default function parseGpx(text) {
  if (typeof text !== 'string' || text.trim() === '') return null;

  let doc;
  try {
    doc = new DOMParser().parseFromString(text, 'application/xml');
  } catch (e) {
    return null;
  }
  // A parse failure is reported as a document containing <parsererror>, not by
  // throwing — so it has to be looked for.
  if (!doc || doc.querySelector('parsererror')) return null;

  for (const tag of POINT_TAGS) {
    const nodes = Array.from(doc.getElementsByTagName(tag));
    if (nodes.length < MIN_POINTS) continue;

    const points = nodes
      .map(node => parsePoint({ lat: node.getAttribute('lat'), lng: node.getAttribute('lon') }))
      .filter(Boolean);

    // Two DISTINCT points, not merely two points. A file that repeats one
    // coordinate — a placeholder, or a device that never got a fix — is not a
    // route: drawing it gives a zero-length line and a map zoomed to its
    // maximum on a single spot, which looks like a broken map rather than like
    // missing data.
    if (points.length >= MIN_POINTS && hasTwoDistinct(points)) return points;
  }

  return null;
}
